import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, finalize, take, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { ToastService } from '../services/toast.service';
import { AuthData } from '../models/auth.model';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private isTokenRequesting = false;
  private tokenSubject: BehaviorSubject<AuthData | null> =
    new BehaviorSubject<AuthData | null>(null);

  constructor(private authService: AuthService, private toastService: ToastService) { }

  public intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Skip auth for auth endpoints
    if (this.isAuthEndpoint(request.url)) {
      return next.handle(request);
    }

    // Add token to request
    const authReq = this.addToken(request);

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        // Don't trigger logout for server errors (500, 502, 503, etc.)
        if (error.status >= 500) {
          return throwError(() => error);
        }

        // Handle 401 errors
        if (error.status === 401) {
          return this.handle401Error(request, next);
        }

        if (error.status === 403) {
          // IP-restricted client and the request came from a new/changed IP
          if (error.error?.title === 'invalid_ipaddress') {
            return this.handleInvalidTokenIp(request, next);
          }

          if (error.error?.title === 'not_authorized') {
            return this.handleForbiddenError(request, next);
          }
        }

        return throwError(() => error);
      })
    );
  }

  private handleForbiddenError(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // If already refreshing/requesting a token, wait for it to complete
    if (this.isRefreshing || this.isTokenRequesting) {
      return this.tokenSubject.pipe(
        filter((result) => result !== null),
        take(1),
        switchMap((authData) => {
          const newReq = this.addTokenToRequest(request, authData!.access_token);
          return next.handle(newReq);
        }),
        catchError(() => {
          return throwError(() => new HttpErrorResponse({ status: 403 }));
        })
      );
    }

    // Use the (long-lived) remember-me API key + device-token to get a fresh access token
    if (this.authService.checkApiKeyValidity()) {
      this.isRefreshing = true;
      this.tokenSubject.next(null);
      this.isTokenRequesting = true;

      return this.loginWithApiKeyAndRetry(request, next).pipe(
        finalize(() => {
          this.isTokenRequesting = false;
        })
      );
    }

    // No valid API key to fall back on - session really is over
    this.toastService.show('Uw sessie is verlopen. U wordt uitgelogd.', 'error');
    this.doLogout();
    return throwError(() => new HttpErrorResponse({ status: 403 }));
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // If already refreshing, wait for it to complete
    if (this.isRefreshing || this.isTokenRequesting) {
      return this.tokenSubject.pipe(
        filter((result) => result !== null),
        take(1),
        switchMap((authData) => {
          const newReq = this.addTokenToRequest(request, authData!.access_token);
          return next.handle(newReq);
        }),
        catchError(() => {
          return throwError(() => new HttpErrorResponse({ status: 401 }));
        })
      );
    }

    // Not currently refreshing, try to refresh
    const authData = this.authService.getAuth();
    if (!authData) {
      this.doLogout();
      return throwError(() => new HttpErrorResponse({ status: 401 }));
    }

    // Try refresh token first
    if (this.authService.checkRefreshTokenValidity()) {
      this.isRefreshing = true;
      this.tokenSubject.next(null);

      return this.authService.refreshToken().pipe(
        switchMap((newAuthData: AuthData) => {
          this.isRefreshing = false;
          this.tokenSubject.next(newAuthData);
          const newReq = this.addTokenToRequest(request, newAuthData.access_token);
          return next.handle(newReq);
        }),
        catchError((error) => {
          this.isRefreshing = false;

          // If refresh fails, try API key
          if (this.authService.checkApiKeyValidity()) {
            return this.loginWithApiKeyAndRetry(request, next);
          }

          // Both failed - replace subject first so new 401s subscribe to the fresh
          // subject, then error the old one to unblock currently-waiting requests
          const oldSubject = this.tokenSubject;
          this.tokenSubject = new BehaviorSubject<AuthData | null>(null);
          oldSubject.error(error);
          this.doLogout();
          return throwError(() => error);
        }),
        finalize(() => {
          this.isRefreshing = false;
        })
      );
    }

    // Refresh token not valid, try API key
    if (this.authService.checkApiKeyValidity()) {
      return this.loginWithApiKeyAndRetry(request, next);
    }

    // Nothing works, logout
    this.doLogout();
    return throwError(() => new HttpErrorResponse({ status: 401 }));
  }

  private handleInvalidTokenIp(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (this.isRefreshing) {
      return this.tokenSubject.pipe(
        filter((result) => result !== null),
        take(1),
        switchMap((authData) => {
          const newReq = this.addTokenToRequest(request, authData!.access_token);
          return next.handle(newReq);
        })
      );
    } else {
      if (this.authService.checkApiKeyValidity()) {
        this.isRefreshing = true;
        this.tokenSubject.next(null);
        this.isTokenRequesting = true;

        return this.loginWithApiKeyAndRetry(request, next).pipe(
          finalize(() => {
            this.isTokenRequesting = false;
          })
        );
      } else {
        this.doLogout();
        return throwError(() => new HttpErrorResponse({ status: 401 }));
      }
    }
  }

  private loginWithApiKeyAndRetry(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const authData = this.authService.getAuth();
    const apiKey = this.authService.getApiKey();

    if (!authData || !apiKey) {
      this.doLogout();
      return throwError(() => new HttpErrorResponse({ status: 401 }));
    }

    return this.authService.loginWithApiKey().pipe(
      switchMap((newAuthData: AuthData) => {
        this.tokenSubject.next(newAuthData);
        const newReq = this.addTokenToRequest(request, newAuthData.access_token);

        // Request new API key
        return this.authService.requestApiKey(authData.device_token || '').pipe(
          switchMap(() => next.handle(newReq))
        );
      }),
      catchError((error) => {
        const oldSubject = this.tokenSubject;
        this.tokenSubject = new BehaviorSubject<AuthData | null>(null);
        oldSubject.error(error);
        this.doLogout();
        return throwError(() => error);
      }),
      finalize(() => {
        this.isRefreshing = false;
      })
    );
  }

  private addToken(request: HttpRequest<any>): HttpRequest<any> {
    const token = this.authService.accessToken();
    if (token) {
      return this.addTokenToRequest(request, token);
    }
    return request;
  }

  private addTokenToRequest(
    request: HttpRequest<any>,
    token: string
  ): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private isAuthEndpoint(url: string): boolean {
    return /oauth|authentication|ipify/i.test(url);
  }

  private doLogout(): void {
    this.authService.logout();
  }
}
